import React, { useState, useEffect } from 'react';
import { getToken } from '../../utils/token';
import './DivisionInfo.css';

export interface Division {
  id: number;
  name: string;
  organizationId: number;
  url?: string;
  employeeCount?: number;
  naics?: string;
  country?: string;
  state?: string;
  city?: string;
  postalCode?: string;
  address1?: string;
  address2?: string;
  annualSpend?: number;
  revenue?: number;
  created: string;
  updated?: string;
  uuid: string;
  taxId?: string;
  wfcId?: number;
  buyerSupplierId?: string;
  isImported?: boolean;
  makerName?: string;
  makerOrganizationUuid?: string;
  organizationUuid?: string;
  organizationName?: string;
  makersTakersOrgainizationsCompanyId?: string;
  uploadDirectory?: string;
  opportunities?: unknown[];
  srm?: unknown[];
  eslap?: {
    divisionId: string;
    companyId: string;
    address2?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    address1?: string;
    city?: string;
    divisionName: string;
  };
}

export interface DivisionInfoProps {
  uuid: string;
  divisionId?: string;
  context?: string;
}

export const DivisionInfo: React.FC<DivisionInfoProps> = ({ uuid, divisionId, context }) => {
  const [division, setDivision] = useState<Division | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDivision = async () => {
      if (!uuid || !divisionId) {
        setError('UUID and Division ID are required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const token = getToken();
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await fetch(`https://fss-gateway.c2fo.com/api/admin/maker/${uuid}/division/${divisionId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch division: ${response.status} ${response.statusText}`);
        }

        const divisionData = await response.json();
        setDivision(divisionData);
      } catch (err) {
        console.error('Error fetching division:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch division data');
      } finally {
        setLoading(false);
      }
    };

    fetchDivision();
  }, [uuid, divisionId]);

  if (loading) {
    return (
      <div className="division-info-container">
        <div className="division-info-header">
          <h3>🏢 Division Information</h3>
          <span className="division-count">Loading...</span>
        </div>
        <div className="division-loading">
          <div className="loading-spinner">⏳</div>
          <p>Fetching division details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="division-info-container">
        <div className="division-info-header">
          <h3>🏢 Division Information</h3>
          <span className="division-count">Error</span>
        </div>
        <div className="division-error">
          <div className="error-icon">❌</div>
          <p>Error loading division: {error}</p>
        </div>
      </div>
    );
  }

  if (!division) {
    return (
      <div className="division-info-container">
        <div className="division-info-header">
          <h3>🏢 Division Information</h3>
          <span className="division-count">No division found</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatAddress = (division: Division) => {
    const parts = [
      division.address1,
      division.address2,
      division.city,
      division.state,
      division.postalCode,
      division.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  return (
    <div className="division-info-container">
      <div className="division-info-header">
        <h3>🏢 Division Information</h3>
        <span className="division-count">1 division</span>
      </div>
      {context && <p className="division-info-context">{context}</p>}

      <div className="divisions-grid">
        <div key={division.id} className="division-card">
          <div className="division-card-header">
            <div className="division-icon">🏢</div>
            <div className="division-name-section">
              <h4 className="division-name">{division.name || 'Unnamed Division'}</h4>
              {division.url && (
                <div className="division-url">
                  <a href={division.url} target="_blank" rel="noopener noreferrer">
                    {division.url}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="division-details">
            <div className="division-detail-item">
              <span className="detail-label">Division ID:</span>
              <span className="detail-value">{division.id}</span>
            </div>

            <div className="division-detail-item">
              <span className="detail-label">UUID:</span>
              <span className="detail-value">{division.uuid}</span>
            </div>

            {division.organizationId !== undefined && (
              <div className="division-detail-item">
                <span className="detail-label">Organization ID:</span>
                <span className="detail-value">{division.organizationId}</span>
              </div>
            )}

            {division.organizationName && (
              <div className="division-detail-item">
                <span className="detail-label">Organization:</span>
                <span className="detail-value">{division.organizationName}</span>
              </div>
            )}

            {division.makerName && (
              <div className="division-detail-item">
                <span className="detail-label">Maker:</span>
                <span className="detail-value">{division.makerName}</span>
              </div>
            )}

            {division.buyerSupplierId && (
              <div className="division-detail-item">
                <span className="detail-label">Buyer Supplier ID:</span>
                <span className="detail-value">{division.buyerSupplierId}</span>
              </div>
            )}

            {division.employeeCount !== undefined && (
              <div className="division-detail-item">
                <span className="detail-label">Employees:</span>
                <span className="detail-value">{new Intl.NumberFormat('en-US').format(division.employeeCount)}</span>
              </div>
            )}

            {division.naics && (
              <div className="division-detail-item">
                <span className="detail-label">NAICS:</span>
                <span className="detail-value">{division.naics}</span>
              </div>
            )}

            {division.taxId && (
              <div className="division-detail-item">
                <span className="detail-label">Tax ID:</span>
                <span className="detail-value">{division.taxId}</span>
              </div>
            )}

            {formatAddress(division) !== 'N/A' && (
              <div className="division-detail-item">
                <span className="detail-label">Address:</span>
                <span className="detail-value">{formatAddress(division)}</span>
              </div>
            )}

            {division.annualSpend !== undefined && (
              <div className="division-detail-item">
                <span className="detail-label">Annual Spend:</span>
                <span className="detail-value">{formatCurrency(division.annualSpend)}</span>
              </div>
            )}

            {division.revenue !== undefined && (
              <div className="division-detail-item">
                <span className="detail-label">Revenue:</span>
                <span className="detail-value">{formatCurrency(division.revenue)}</span>
              </div>
            )}

            {division.isImported !== undefined && (
              <div className="division-detail-item">
                <span className="detail-label">Imported:</span>
                <span className="detail-value">{division.isImported ? 'Yes' : 'No'}</span>
              </div>
            )}
          </div>

          {(division.created || division.updated) && (
            <div className="division-timestamps">
              {division.created && (
                <div className="timestamp">
                  <span className="timestamp-label">Created:</span>
                  <span className="timestamp-value">{formatDate(division.created)}</span>
                </div>
              )}
              {division.updated && (
                <div className="timestamp">
                  <span className="timestamp-label">Updated:</span>
                  <span className="timestamp-value">{formatDate(division.updated)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
